import { Component, OnInit, OnDestroy, Injectable, Inject, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActionCommandService, ActionCommand } from '../../../../services/action-command.service';
import { MenuStateService } from '../../../../services/menu-state.service';
import { SaleService } from '../../../../services/sale.service';
import { ComandaService } from '../../../../services/comanda.service';
import { PrintService } from '../../../../services/print.service';
import { PaymentAmountInputComponent } from '../payment-amount-input/payment-amount-input.component';
import { CreateSaleFromPdvDto } from '../../create-sale-from-pdv-dto';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

export interface PaymentData {
  method: string;
  amount: number;
}

export interface PaymentMethodSelectionInput {
  saleData: CreateSaleFromPdvDto;
  totalSale: number;
}

@Injectable()
@Component({
  selector: 'app-payment-method-selection',
  imports: [CommonModule, FormsModule, PaymentAmountInputComponent, ToastModule, ConfirmDialogModule],
  templateUrl: './payment-method-selection.component.html'
})
export class PaymentMethodSelectionComponent implements OnInit, OnDestroy, ActionCommand {
  
  totalSale: number = 0;
  totalPaid: number = 0;
  remainingAmount: number = 0;
  exchange: number = 0;
  selectedPaymentMethod: string | null = null;
  payments: PaymentData[] = [];
  saleData: CreateSaleFromPdvDto | null = null;
  isFinishingSale = false;
  /** Se a venda veio de uma comanda (mesa), fechar a comanda após criar a venda. */
  comandaIdToClose: number | null = null;

  @Output() clearPdvRequested = new EventEmitter<void>();
  @Output() menuCloseRequested = new EventEmitter<void>();
  @Output() crediarioRequested = new EventEmitter<{ payments: PaymentData[]; remainingAmount: number }>();

  constructor(
    @Inject(ActionCommandService) private actionCommandService: ActionCommandService,
    @Inject(MenuStateService) private menuStateService: MenuStateService,
    @Inject(SaleService) private saleService: SaleService,
    @Inject(ComandaService) private comandaService: ComandaService,
    @Inject(PrintService) private printService: PrintService,
    @Inject(MessageService) private messageService: MessageService,
    @Inject(ConfirmationService) private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.registerCommands();
    this.calculateAmounts();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Ignorar se estiver digitando em um input
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    const key = event.key.toUpperCase();
    
    // Mapeamento de atalhos
    switch (key) {
      case 'D':
        this.selectPaymentMethod('MONEY');
        event.preventDefault();
        break;
      case 'T':
        this.selectPaymentMethod('BANK_TRANSFER');
        event.preventDefault();
        break;
      case 'P':
        this.selectPaymentMethod('PIX');
        event.preventDefault();
        break;
      case 'S':
        this.selectPaymentMethod('BANK_TRANSFER');
        event.preventDefault();
        break;
      case 'B':
        this.selectPaymentMethod('DEBIT_CARD');
        event.preventDefault();
        break;
      case 'E':
        this.selectPaymentMethod('CHECK');
        event.preventDefault();
        break;
      case 'C':
        this.selectPaymentMethod('CREDIT_CARD');
        event.preventDefault();
        break;
      case 'V':
        this.selectPaymentMethod('VOUCHER');
        event.preventDefault();
        break;
      case 'O':
        this.selectPaymentMethod('CREDIARIO');
        event.preventDefault();
        break;
    }
  }

  // Método para receber dados da venda do componente pai (comandaId opcional: fechar comanda após criar venda)
  setSaleData(saleData: CreateSaleFromPdvDto, totalSale: number, comandaId?: number | null): void {
    this.saleData = saleData;
    this.totalSale = totalSale;
    this.remainingAmount = totalSale;
    this.comandaIdToClose = comandaId ?? null;
  }

  ngOnDestroy(): void {
    this.unregisterCommands();
  }

  private registerCommands(): void {
    // Registrar comando F1 para finalizar venda
    this.actionCommandService.registerCommand({
      getKey: () => 'F1',
      execute: () => this.finishSale(),
      getDescription: () => 'Finalizar Venda'
    });

    // Não registrar ESC aqui - deixar o PDV gerenciar ESC
    // O cancelamento agora é feito via evento menuCloseRequested
  }

  private unregisterCommands(): void {
    // Desregistrar apenas F1 - não desregistrar ESC pois o PDV gerencia
    this.actionCommandService.unregisterCommand('F1');
  }

  selectPaymentMethod(method: string): void {
    if (method === 'CREDIARIO') {
      this.crediarioRequested.emit({
        payments: [...this.payments],
        remainingAmount: this.remainingAmount
      });
      // Mantém abertura direta como fallback para garantir transição de tela
      // mesmo se o binding do evento no pai não disparar em algum cenário.
      this.menuStateService.openCreditoMenu();
    } else {
      this.selectedPaymentMethod = method;
    }
  }

  onPaymentCompleted(payment: PaymentData): void {
    this.payments.push(payment);
    this.calculateAmounts();
    this.selectedPaymentMethod = null;
    
    // Verificar se o valor pago é suficiente para finalizar a venda
    if (this.remainingAmount <= 0) {
      this.finishSale();
    }
  }

  onPaymentCancel(): void {
    this.selectedPaymentMethod = null;
  }

  private calculateAmounts(): void {
    this.totalPaid = this.payments.reduce((sum, payment) => sum + payment.amount, 0);
    this.remainingAmount = this.totalSale - this.totalPaid;
    this.exchange = this.totalPaid > this.totalSale ? this.totalPaid - this.totalSale : 0;
  }

  finishSale(): void {
    if (this.isFinishingSale) {
      return;
    }

    if (this.remainingAmount <= 0 && this.saleData) {
      this.isFinishingSale = true;
      console.log('Finalizando venda com pagamentos:', this.payments);
      
      // Preparar dados da venda com os pagamentos
      const saleToCreate = this.prepareSaleData();
      
      // Chamar serviço para criar a venda
      console.log(saleToCreate);
      this.saleService.create(saleToCreate).subscribe({
        next: (response) => {
          console.log('Venda criada com sucesso:', response);
          const saleId = response?.data?.saleId;
          const comandaId = this.comandaIdToClose;
          if (comandaId != null) {
            this.comandaService.fechar(comandaId).subscribe({
              next: () => {
                this.finalizeSuccessfulSaleFlow(saleId);
              },
              error: (err) => {
                console.error('Erro ao fechar comanda:', err);
                this.finalizeSuccessfulSaleFlow(saleId);
              }
            });
          } else {
            this.finalizeSuccessfulSaleFlow(saleId);
          }
        },
        error: (error) => {
          this.isFinishingSale = false;
          console.error('Erro ao criar venda:', error);
          const errorMessage = error?.error?.message || 'Não foi possível finalizar a venda. Por favor, tente novamente.';
          
          // Verificar se o serviço está disponível
          if (!this.messageService) {
            console.error('MessageService não está disponível');
            alert('Erro: ' + errorMessage);
          } else {
            // Mostrar mensagem de erro
            this.messageService.add({ 
              severity: 'error', 
              summary: 'Erro!', 
              detail: errorMessage, 
              life: 5000 
            });
          }
        }
      });
    }
  }

  private finalizeSuccessfulSaleFlow(saleId?: number): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso!',
      detail: 'Venda finalizada com sucesso!',
      life: 3000
    });

    this.confirmationService.confirm({
      message: 'Deseja imprimir o cupom da venda?',
      header: 'Impressão',
      icon: 'pi pi-print',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      defaultFocus: 'accept',
      accept: () => {
        if (saleId != null && saleId > 0) {
          this.printService.printSaleReceipt(saleId);
        }
      }
    });

    const tryFocusAccept = (attempt: number = 0) => {
      const acceptButton = document.querySelector('.p-confirm-dialog-accept') as HTMLButtonElement;
      if (acceptButton) {
        acceptButton.focus();
        setTimeout(() => {
          if (document.activeElement !== acceptButton) {
            acceptButton.focus();
          }
        }, 50);
      } else if (attempt < 5) {
        setTimeout(() => {
          tryFocusAccept(attempt + 1);
        }, 100 * (attempt + 1));
      }
    };

    setTimeout(() => {
      tryFocusAccept(0);
    }, 150);

    this.clearPdv();
    this.menuStateService.closeAdminMenu();
    this.isFinishingSale = false;
  }

  private prepareSaleData(): any {
    if (!this.saleData) {
      throw new Error('Dados da venda não disponíveis');
    }

    // Preparar dados da venda com os pagamentos
    const saleData = {
      ...this.saleData,
      // Mapear pagamentos para os campos específicos do modelo Sale
      moneyTotal: this.getPaymentTotal('MONEY'),
      pixTotal: this.getPaymentTotal('PIX'),
      creditCardTotal: this.getPaymentTotal('CREDIT_CARD'),
      debitCardTotal: this.getPaymentTotal('DEBIT_CARD'),
      checkTotal: this.getPaymentTotal('CHECK'),
      creditTotal: this.getPaymentTotal('CREDIT'),
      bankTransferTotal: this.getPaymentTotal('BANK_TRANSFER'),
      voucherTotal: this.getPaymentTotal('VOUCHER'),
    };

    return saleData;
  }

  private getPaymentTotal(method: string): number {
    return this.payments
      .filter(payment => payment.method === method)
      .reduce((sum, payment) => sum + payment.amount, 0);
  }

  private clearPdv(): void {
    this.clearPdvRequested.emit();
  }

  cancel(): void {
    // Emitir evento para fechar o menu no componente pai
    this.menuCloseRequested.emit();
    console.log('Cancelando fechamento de venda');
  }

  // Implementação da interface ActionCommand
  getKey(): string {
    return 'F1';
  }

  execute(): void {
    this.finishSale();
  }

  getDescription(): string {
    return 'Finalizar Venda';
  }
}
